import { useState, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { Person, CheckCircle, Error } from "@mui/icons-material";
import { useForm, SubmitHandler } from "react-hook-form";
import { useForgotPasswordByUsernameMutation } from "features/auth/auth";
import AppDialog from "./common/AppDialog";

interface ForgotPasswordModalProps {
  open: boolean;
  onClose: () => void;
  initialUsername?: string;
}

type ForgotPasswordInputs = {
  username: string;
};

export default function ForgotPasswordModal({
  open,
  onClose,
  initialUsername = "",
}: ForgotPasswordModalProps) {
  const [forgotPassword, { isLoading }] = useForgotPasswordByUsernameMutation();
  const [resultState, setResultState] = useState<"idle" | "success" | "error">("idle");
  const [resultMessage, setResultMessage] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<ForgotPasswordInputs>();

  useEffect(() => {
    if (open && initialUsername) {
      setValue("username", initialUsername);
    }
  }, [open, initialUsername, setValue]);

  const onSubmit: SubmitHandler<ForgotPasswordInputs> = async (data) => {
    try {
      const response = await forgotPassword({ username: data.username }).unwrap();
      if (response.has_email) {
        setMaskedEmail(response.masked_email || "");
        setResultMessage(response.message);
        setResultState("success");
      } else {
        setResultMessage(response.message);
        setResultState("error");
      }
    } catch (error: any) {
      const message = error?.data?.message || "Something went wrong. Please try again.";
      setResultMessage(message);
      setResultState("error");
    }
  };

  const handleClose = () => {
    setResultState("idle");
    setResultMessage("");
    setMaskedEmail("");
    reset();
    onClose();
  };

  const handleTryAgain = () => {
    setResultState("idle");
    setResultMessage("");
    setMaskedEmail("");
  };

  const showUsernameInput = !initialUsername;

  if (resultState === "success") {
    return (
      <AppDialog
        open={open}
        onClose={handleClose}
        title="Password reset link sent"
        showCloseIcon
        maxWidth="sm"
        actions={
          <Button
            variant="contained"
            onClick={handleClose}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Close
          </Button>
        }
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <CheckCircle sx={{ color: "#4caf50", fontSize: 20, flexShrink: 0 }} />
          <Box>
            <Typography variant="body2" color="text.secondary">
              A password reset link has been sent to <strong style={{ color: "#000" }}>{maskedEmail}</strong>.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Please check your inbox and follow the instructions to reset your password.
            </Typography>
          </Box>
        </Box>
      </AppDialog>
    );
  }

  if (resultState === "error") {
    return (
      <AppDialog
        open={open}
        onClose={handleClose}
        title="Unable to reset password"
        showCloseIcon
        maxWidth="sm"
        actions={
          <>
            <Button onClick={handleTryAgain} sx={{ textTransform: "none" }}>
              Try Again
            </Button>
            <Button
              variant="contained"
              onClick={handleClose}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Close
            </Button>
          </>
        }
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Error sx={{ color: "#d32f2f", fontSize: 20, flexShrink: 0 }} />
          <Typography variant="body2" color="text.secondary">
            {resultMessage}
          </Typography>
        </Box>
      </AppDialog>
    );
  }

  return (
    <AppDialog
      open={open}
      onClose={handleClose}
      title="Forgot Password?"
      showCloseIcon
      maxWidth="sm"
      actions={
        <>
          <Button onClick={handleClose} sx={{ textTransform: "none" }}>
            Back to login
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            disabled={isLoading}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {isLoading ? "Sending…" : "Send Reset Link"}
          </Button>
        </>
      }
    >
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {showUsernameInput
            ? "Enter your username to reset your password."
            : "Click the button below to receive a password reset link."}
        </Typography>

        {showUsernameInput && (
          <TextField
            fullWidth
            id="username"
            label="Username"
            autoComplete="username"
            autoFocus
            error={Boolean(errors.username)}
            helperText={errors.username?.message}
            InputProps={{
              startAdornment: <Person sx={{ color: "gray", mr: 1 }} />,
            }}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
            {...register("username", {
              required: "Username is required",
            })}
          />
        )}

        {!showUsernameInput && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Username: <strong>{initialUsername}</strong>
          </Typography>
        )}
      </Box>
    </AppDialog>
  );
}
